import type { EAN } from '../shared/ean';

/**
 * What one `brand_compare` run is asked for.
 *
 * The codes are the real ones. The backend worker reads `medpdb2` and therefore asks in that
 * table's stored (masked) key form, and encoding them is the adapter's business -- a stored
 * key does not appear above the data layer (.claude/rules/portal-domain.md).
 *
 * `vendors`, `start` and `end` ride through untouched: the worker owns the `params` contract
 * and lives in no repo here, so nothing on this side reshapes it.
 */
export type BrandCompareRequest = {
  readonly eans: readonly EAN[];
  readonly vendors: readonly string[];
  readonly start: string;
  readonly end: string;
  /** Who is asking. The queue row's `created_by`; a caller's fact, not the adapter's. */
  readonly requestedBy: string;
  /** Checked between polls; true abandons the wait. The caller's teardown, nothing else. */
  readonly shouldStop?: () => boolean;
};

/**
 * The document a finished run left behind.
 *
 * Null is "the run left none" -- the queue would not take the row, the caller is not in a
 * browser, or the job wrote no file. It is wrapped rather than handed back bare so that
 * absence stays distinguishable from a document that is itself `null`, which a bare return
 * would collapse into the same value and settle a row on.
 */
export type BrandCompareResult = { readonly document: unknown } | null;

/**
 * The queue boundary to the backend report workers (`docs/ARCHITECTURE.md` section 3): the
 * frontend asks for a run and reads the result back, and never computes one.
 */
export abstract class ReportJobRepository {
  /**
   * Runs one `brand_compare` job to completion: queues it, waits for the worker, and reads
   * back the document it wrote.
   *
   * Rejects when the job itself finished `failed`, and lets the wait's own abort and timeout
   * through untouched: which of those ends a caller's work silently is the caller's policy,
   * not this port's.
   */
  abstract runBrandCompare(request: BrandCompareRequest): Promise<BrandCompareResult>;
}
